<script setup>
import { onMounted, computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";

import ServiceOverviewFilters from "@/components/employability/filters/ServiceOverviewFilters.vue";

import debounce from "lodash/debounce";
import { useEmployabilityStore } from "@/stores/employability";

const employabilityStore = useEmployabilityStore();

const router = useRouter();

const data = computed(() => employabilityStore.serviceOverview);

const params = computed(() => employabilityStore.serviceOverviewRequestQueryParams);

const pagination = computed(() => employabilityStore.serviceOverviewPagination);

const isFetching = ref(false);

const expandedItems = ref([]);

const formatDate = (date) => {
  if (date === null) {
    return date;
  }
  return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY");
};

const getEmployability = async () => {
  isFetching.value = true;

  params.value.set("page", pagination.value.currentPage - 1);
  params.value.set("size", pagination.value.pageSize);

  await employabilityStore.getEmployabilityServiceOverview(params.value);

  isFetching.value = false;
};

const download = ref(false);

const exportTable = async () => {
  download.value = true;
  const resp = await employabilityStore.downloadExcel(params.value);

  const url = window.URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "employability-service-overview.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  download.value = false;
};

const checkClickEvent = (event) => {
  const check = event.target && event.target.classList.contains("mdi-pencil");
  return check;
};

const edit = (item, event) => {
  const check = checkClickEvent(event);
  if (check) {
    router.push({
      path: `/employability/edit/${item}`,
    });
  }
};

const toggleExpanded = (item) => {
  const index = expandedItems.value.findIndex(expandedItem => expandedItem.id === item.id);
  if (index > -1) {
    expandedItems.value.splice(index, 1);
  } else {
    expandedItems.value.push(item);
  }
};

const isExpanded = (item) => {
  return expandedItems.value.some(expandedItem => expandedItem.id === item.id);
};

const employabilityDebounced = debounce(getEmployability, 500);

watch(
  pagination,
  () => {
    if (!isFetching.value) {
      getEmployability();
    }
  },
  {
    deep: true,
  }
);

watch(
  params,
  (newParams) => {
    console.log('Filters changed, new params:', newParams.toString());
    employabilityDebounced();
  },
  {
    deep: true,
  }
);

const mainHeaders = [
  {
    title: "",
    key: "data-table-expand",
    width: "50px",
    sortable: false,
  },
  {
    title: "Actions",
    key: "actions",
    align: "start",
    width: "70px",
    sortable: false,
  },
  { title: "Date", key: "date", width: "120px" },
  { title: "Title", key: "title", width: "200px" },
  { title: "Beneficiary", key: "beneficiary", width: "150px" },
  { title: "Email", key: "beneficiaryEmail", width: "180px" },
  { title: "Telephone", key: "beneficiaryTelephone", width: "130px" },
  { title: "Courses", key: "courses", width: "200px" },
  { title: "Responsible", key: "responsible", width: "140px" },
  { title: "Service Name", key: "service", width: "150px" },
];

const nestedHeaders = [
  { title: "Position ID", key: "id", width: "100px" },
  { title: "Job Position", key: "title", width: "250px" },
  { title: "Company", key: "company", width: "180px" },
  { title: "TA", key: "ta", width: "150px" },
  { title: "Status", key: "status", width: "120px" },
];

onMounted(() => {
  getEmployability();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-10 page-title">
          Employability Service Overview
        </h1>
        <ServiceOverviewFilters />
        <div class="d-flex justify-space-between align-center mb-5">
          <h3>Total Records: {{ pagination.totalItems }}</h3>
          <v-btn
            color="employability"
            :loading="download"
            :disabled="download"
            @click="exportTable()"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-file-excel</v-icon>
              <span>Export</span>
            </div>
          </v-btn>
        </div>
        
        <!-- Main Service Table -->
        <v-data-table
          :items="data"
          :headers="mainHeaders"
          :hide-default-footer="true"
          :loading="isFetching"
          class="employability-table bordered-columns"
          item-value="id"
          :expanded="expandedItems"
          show-expand
          @update:expanded="expandedItems = $event"
        >

          <template #item.actions="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                color="employability"
                @click="edit(item.id, $event)"
              />
            </div>
          </template>

          <template #item.title="{ item }">
            <router-link
              :to="'/employability/view/' + item.id"
              target="_blank"
              class="chip-link-color-employability"
            >
              <v-chip> {{ item.title }} </v-chip>
            </router-link>
          </template>

          <template #item.date="{ item }">
            <span style="white-space: nowrap">{{ formatDate(item.date) }}</span>
          </template>

          <template #item.beneficiary="{ item }">
            <router-link
              :to="'/beneficiary/intake/view/' + item.beneficiaryId"
              target="_blank"
              class="chip-link-color"
            >
              <v-chip> {{ item.beneficiary }} </v-chip>
            </router-link>
          </template>

          <template #item.courses="{ item }">
            <div
              v-for="course in item.courses"
              :key="course.id"
              class="mb-1"
            >
              <router-link
                :to="'/academy/view/' + course.id"
                target="_blank"
                class="chip-link-color-academy"
              >
                <v-chip size="small"> {{ course.cycle }} </v-chip>
              </router-link>
            </div>
          </template>

          <template #item.service="{ item }">
            <v-chip color="employability" variant="tonal">
              {{ item.service }}
            </v-chip>
          </template>

          <!-- Expanded Row Template for Job Positions -->
          <template #expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length" class="pa-0">
                <div class="nested-table-container">
                  <h4 class="mb-3">Job Positions for {{ item.beneficiary }}</h4>
                  
                  <div v-if="item.jobPositions && item.jobPositions.length > 0">
                    <v-data-table
                      :items="item.jobPositions"
                      :headers="nestedHeaders"
                      :hide-default-footer="true"
                      class="nested-table"
                      density="compact"
                    >
                      <template #item.id="{ item: job }">
                        <span class="font-weight-bold">{{ job.id }}</span>
                      </template>

                      <template #item.title="{ item: job }">
                        <router-link
                          :to="'/company/job-position/view/' + job.id"
                          target="_blank"
                          class="chip-link-color-company"
                        >
                          <v-chip size="small"> {{ job.title }} </v-chip>
                        </router-link>
                      </template>

                      <template #item.status="{ item: job }">
                        <v-chip 
                          :color="job.status === 'hired' ? 'success' : 
                                 job.status === 'matched' ? 'warning' : 
                                 job.status === 'selected' ? 'info' : 'default'"
                          size="small"
                          variant="tonal"
                        >
                          {{ job.status }}
                        </v-chip>
                      </template>

                      <template #item.company="{ item: job }">
                        <span>{{ job.company || 'N/A' }}</span>
                      </template>

                      <template #item.ta="{ item: job }">
                        <span>{{ job.ta || 'N/A' }}</span>
                      </template>
                    </v-data-table>
                  </div>
                  
                  <div v-else class="pa-4 text-center text-grey">
                    <v-icon>mdi-briefcase-off</v-icon>
                    <p class="mt-2">No job positions found for this beneficiary</p>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table>

        <div class="mt-5">
          <v-pagination
            v-model="pagination.currentPage"
            :length="pagination.pageCount"
            :total-visible="5"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-employability));
}

.v-table {
  table-layout: fixed;

  td,
  th {
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    white-space: normal;
  }
}

.nested-table-container {
  background-color: rgba(var(--v-theme-employability), 0.05);
  padding: 20px;
  border-left: 4px solid rgb(var(--v-theme-employability));

  h4 {
    color: rgb(var(--v-theme-employability));
    font-weight: 600;
  }
}

.nested-table {
  background-color: transparent;
  
  :deep(.v-table__wrapper) {
    box-shadow: none;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  :deep(.v-data-table__th) {
    background-color: rgba(var(--v-theme-employability), 0.1) !important;
    font-weight: 600 !important;
  }
}

.text-grey {
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>